import React, { useEffect, useState } from 'react';
import { Card, Table, Button, Modal, Form, Input, message, Popconfirm, Space, Typography, Tag, Switch } from 'antd';
import { PlusOutlined, DeleteOutlined, UserOutlined, PhoneOutlined, LockOutlined, SearchOutlined, ReloadOutlined, SafetyCertificateOutlined } from '@ant-design/icons';
import { getUsers, saveUser, deleteUser, verifyAllUsers, updateUserStatus } from '../services/api';

const { Title } = Typography;

const statusConfig = {
  active: { color: 'green', text: '可用' },
  disabled: { color: 'red', text: '不可用' },
  unknown: { color: 'default', text: '未验证' },
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [verifying, setVerifying] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getUsers();
      if (response.status === 'success') {
        setUsers(response.data || []);
      } else {
        message.error(response.message || '获取用户列表失败');
      }
    } catch (error) {
      console.error('获取用户列表出错:', error);
      message.error('获取用户列表失败，请稍后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const showAddUserModal = () => {
    setModalVisible(true);
    form.resetFields();
  };

  const hideModal = () => {
    setModalVisible(false);
    form.resetFields();
  };

  const handleSaveUser = async (values) => {
    setSubmitting(true);
    const hideLoading = message.loading('正在验证平台账号，请稍候...', 0);
    try {
      const { name, phone, password } = values;
      const response = await saveUser(name, phone, password);
      hideLoading();
      
      if (response.status === 'success') {
        message.success(response.message || '用户保存成功');
        hideModal();
        fetchUsers();
      } else {
        message.error(response.message || '保存用户失败');
      }
    } catch (error) {
      hideLoading();
      console.error('保存用户出错:', error);
      message.error(error.message || '保存用户失败，请稍后重试');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteUser = async (user) => {
    try {
      if (!user.uid) {
        message.error('用户ID不存在，无法删除');
        return;
      }
      
      const response = await deleteUser(user.uid);
      
      if (response.status === 'success') {
        message.success(response.message || '用户删除成功');
        fetchUsers();
      } else {
        message.error(response.message || '删除用户失败');
      }
    } catch (error) {
      console.error('删除用户出错:', error);
      message.error('删除用户失败，请稍后重试');
    }
  };

  const handleVerifyAllUsers = async () => {
    setVerifying(true);
    const hideLoading = message.loading('正在验证所有用户账号状态，请稍候...', 0);
    try {
      const response = await verifyAllUsers();
      hideLoading();
      if (response.status === 'success') {
        const results = response.data || [];
        const activeCount = results.filter(r => r.status === 'active').length;
        const disabledCount = results.filter(r => r.status === 'disabled').length;
        message.success(`验证完成：${activeCount} 个可用，${disabledCount} 个不可用`);
        fetchUsers();
      } else {
        message.error('验证用户状态失败');
      }
    } catch (error) {
      hideLoading();
      console.error('验证用户状态出错:', error);
      message.error('验证用户状态失败，请稍后重试');
    } finally {
      setVerifying(false);
    }
  };

  const handleToggleStatus = async (user, checked) => {
    const newStatus = checked ? 'active' : 'disabled';
    try {
      const response = await updateUserStatus(user.uid, newStatus);
      if (response.status === 'success') {
        message.success(response.message);
        fetchUsers();
      } else {
        message.error(response.message || '更新状态失败');
      }
    } catch (error) {
      console.error('更新用户状态出错:', error);
      message.error('更新用户状态失败');
    }
  };

  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      render: (text) => (
        <Space>
          <UserOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      render: (text) => (
        <Space>
          <PhoneOutlined />
          {text}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status, record) => {
        const cfg = statusConfig[status] || statusConfig.unknown;
        return (
          <Space>
            <Tag color={cfg.color}>{cfg.text}</Tag>
            <Switch
              size="small"
              checked={status !== 'disabled'}
              onChange={(checked) => handleToggleStatus(record, checked)}
            />
          </Space>
        );
      },
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (text) => text ? new Date(text.replace(' ', 'T')).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-',
    },
    {
      title: '更新时间',
      dataIndex: 'updated_at',
      key: 'updated_at',
      render: (text) => text ? new Date(text.replace(' ', 'T')).toLocaleString('zh-CN', { month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }) : '-',
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Popconfirm
          title="确定要删除这个用户吗？"
          description="删除后无法恢复，请谨慎操作。"
          onConfirm={() => handleDeleteUser(record)}
          okText="确认"
          cancelText="取消"
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
          >
            删除
          </Button>
        </Popconfirm>
      ),
    },
  ];

  const activeCount = users.filter((user) => user.status !== 'disabled').length;

  return (
    <div className="available-list-page">
      <Card>
        <div className="user-mgmt-header">
          <div className="user-mgmt-header-left page-title-group">
            <div>
              <Title level={4} style={{ margin: 0 }}>预约用户</Title>
              <div className="page-title-meta">
                <Tag color="blue">共 {users.length} 人</Tag>
                <Tag color="green">{activeCount} 人可用</Tag>
              </div>
            </div>
            <Input
              placeholder="搜索用户名称或手机号"
              prefix={<SearchOutlined style={{ color: 'var(--text-slate-400)' }} />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              allowClear
              style={{ width: 240 }}
            />
          </div>
          <Space>
            <Button
              icon={<SafetyCertificateOutlined />}
              onClick={handleVerifyAllUsers}
              loading={verifying}
            >
              验证全部状态
            </Button>
            <Button
              icon={<ReloadOutlined />}
              onClick={fetchUsers}
              loading={loading}
            >
              刷新
            </Button>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={showAddUserModal}
            >
              新增用户
            </Button>
          </Space>
        </div>

      <Table
        columns={columns}
        dataSource={users.filter(user => {
          if (!searchText) return true;
          const keyword = searchText.toLowerCase();
          return (user.name || '').toLowerCase().includes(keyword) || (user.phone || '').includes(keyword);
        })}
        rowKey="uid"
        loading={loading}
        scroll={{ x: 'max-content' }}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total) => `共 ${total} 个用户`,
        }}
      />

      <Modal
        title="新增用户"
        open={modalVisible}
        onCancel={hideModal}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveUser}
        >
          <Form.Item
            name="name"
            label="姓名"
            rules={[
              { required: true, message: '请输入姓名' },
              { min: 2, message: '姓名至少2个字符' },
              { max: 10, message: '姓名不能超过10个字符' }
            ]}
          >
            <Input 
              placeholder="请输入姓名"
              prefix={<UserOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="phone"
            label="手机号"
            rules={[
              { required: true, message: '请输入手机号' },
              { pattern: /^1[3-9]\d{9}$/, message: '请输入有效的手机号' }
            ]}
          >
            <Input 
              placeholder="请输入11位手机号"
              prefix={<PhoneOutlined />}
            />
          </Form.Item>

          <Form.Item
            name="password"
            label="平台密码（可选）"
            rules={[
              { min: 4, message: '密码至少4位' }
            ]}
            extra="留空将使用默认密码 654321 进行验证"
          >
            <Input.Password 
              placeholder="请输入预约平台密码（默认 654321）"
              prefix={<LockOutlined />}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={hideModal}>
                取消
              </Button>
              <Button 
                type="primary" 
                htmlType="submit"
                loading={submitting}
              >
                保存
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
      </Card>
    </div>
  );
};

export default UserManagement;
